import { Fragment } from 'react';
import dayjs from 'dayjs';

import { resolveActionDestination } from './actionDestination';

export const formatDate = (date) => {
  if (!date) return 'N/A';

  if (!(date instanceof Date)) {
    date = new Date(date);
  }

  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');

  return `${month}/${day}`;
}

export const formatEmailSenderName = (from = '', fromName = '') => {
  if (fromName?.trim()) {
    return fromName.trim();
  }

  const trimmedFrom = from?.trim() || '';
  const nameMatch = trimmedFrom.match(/^"?([^"<]+)"?\s*<[^>]+>$/);
  if (nameMatch?.[1]?.trim()) {
    return nameMatch[1].trim();
  }

  const emailOnlyMatch = trimmedFrom.match(/^<([^>]+)>$/);
  if (emailOnlyMatch?.[1]) {
    return emailOnlyMatch[1].split('@')[0];
  }

  if (trimmedFrom.includes('@') && !trimmedFrom.includes('<')) {
    return trimmedFrom.split('@')[0];
  }

  return trimmedFrom;
};

export const formatEmailDate = (date) => {
  const now = dayjs();
  const isToday = dayjs(date).isSame(now, 'day');

  if (isToday) {
    // Format as "H:mm A" for a 12-hour clock
    return dayjs(date).format('h:mm A');
  }

  // Format as "MM/DD h:mm A" for other days
  return dayjs(date).format('MM/DD');
};

export const formatEmailDateDetailed = (date) => {
  // Output format: Fri 10/2/2026 11:47 AM
  if (!date) return '';
  const dayjsObj = dayjs(date);
  if (!dayjsObj.isValid()) return '';

  const dayAbbr = dayjsObj.format('ddd'); // e.g., "Fri"
  const month = dayjsObj.format('M'); // 1-based month (no leading zero)
  const day = dayjsObj.format('D'); // day of month (no leading zero)
  const year = dayjsObj.format('YYYY');
  const time = dayjsObj.format('h:mm A'); // 12-hour format

  return `${dayAbbr} ${month}/${day}/${year} ${time}`;
};


export const formatTaskDate = (date, fullDate = false) => {
  // Format as "MM/DD h:mm A" for other days
  const formattedDate = dayjs(date).format(fullDate ? 'MM/DD/YYYY h:mm A' : 'MM/DD');
  if (!formattedDate || formattedDate === 'Invalid Date') {
    return '';
  }
  return formattedDate;
};

export const formatDateInput = (date) => {
  if (!date) return '';

  const parsedDate = new Date(date);
  if (isNaN(parsedDate)) {
    return '';
  }

  return parsedDate.toISOString().split('T')[0];
}

export const formatEstimation = (minutes) => {
  if (minutes >= 1440) { // 24 hours
    return `${Math.floor(minutes / 1440)} Days`
  }
  if (minutes >= 60) {
    return `${Math.floor(minutes / 60)} Hours`
  }
  return `${minutes} Mins`
}

export const decodeHtmlEntities = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return doc.documentElement.textContent;
}  

export const formatEmailText = (string) => {
  if (typeof string === 'string') {
    return string
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n')
      .map((line, idx, arr) => (
        <Fragment key={idx}>
          {line}
          {idx < arr.length - 1 && <br />}
        </Fragment>
      ))
  }
  return string;
}

export const formatTitleCase = (str) => {
  return str.split(' ').map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()).join(' ');
}

export const formatActionTypeLabel = (action, digest) => {
  switch (action.type) {
    case 'archive':
      return 'Archived';
    case 'reply':
      return 'Reply Drafted';
    case 'forward':
      return 'Forward Drafted';
    case 'markRead':
      return 'Marked as Read';
    case 'move-to-label': {
      const destination = resolveActionDestination(action, digest);
      return `Moved to ${destination?.label || 'Label'}`;
    }
    case 'create-a-task':
      return 'Created a Task';
    case 'create-a-maintenance-ticket':
      return 'Created a Maintenance Ticket';
    case 'add-to-project':
      return 'Added to Project';
    case 'complete':
      return 'Marked Complete';
    case 'reschedule':
      return 'Reschedule Required';
    case 'mark-blocked':
      return 'Task Blocked';
    case 'raise-priority':
      return 'Raise Priority';
    case 'drop-priority':
      return 'Drop Priority';
    case 'raise-question':
      return 'Question Raised';
    case 'raise-issue':
      return 'Issue Raised';
    default:
      return action.type;
  }
} 
